// Dựng đầu vào tính lương của một kỳ từ dữ liệu đã nạp. Hàm thuần, không gọi API.
// Chuyển từ resolvePayrollDraftEmployees / loadPayrollBaseSources / buildPayrollEmployeeInputs trong
// VUONGDATWOOD_V2/src/features/cong-luong/action.ts; object dùng tên trường theo src/payroll/data/tables.js.
import { calculatePayrollPeriod } from './calculation';
import { canonicalPayrollCode, resolveEffectiveDatedRow } from './domain';
import { compilePayrollTemplate } from './templateCompiler';
import { normalizeTeamKey, resolvePayrollTemplateForEmployee } from './templateResolver';
import { countPaidHolidayDays, paidLeaveDaysByEmployee } from './leaveHoliday';

const WORK_STATUS_LEFT = 'Đã nghỉ';

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

function coversDate(row, at) {
    const day = startOfDay(at);
    return (!row.HIEU_LUC_TU || startOfDay(row.HIEU_LUC_TU) <= day)
        && (!row.HIEU_LUC_DEN || startOfDay(row.HIEU_LUC_DEN) >= day);
}

/**
 * Nhân sự có làm việc trong kỳ: bắt đầu trước cuối kỳ và nghỉ sau đầu kỳ (như V2). Riêng người ghi
 * "Đã nghỉ" mà không có ngày nghỉ thì không biết nghỉ từ bao giờ, coi như không thuộc kỳ.
 */
export function isEmployeeInPeriod(employee, range) {
    if (employee.TRANG_THAI_LAM_VIEC === WORK_STATUS_LEFT && !employee.NGAY_NGHI) return false;
    if (employee.NGAY_BAT_DAU && startOfDay(employee.NGAY_BAT_DAU) > startOfDay(range.to)) return false;
    if (employee.NGAY_NGHI && startOfDay(employee.NGAY_NGHI) < startOfDay(range.from)) return false;
    return true;
}

const compareTeams = (left, right) => left.localeCompare(right, 'vi', { numeric: true });

/**
 * Các tổ của mỗi nhân sự tại ngày `at`: các tổ người đó đứng tên phụ trách trong TO_PBNS. Như V2,
 * phân bổ là nơi duy nhất nói ai làm tổ nào; NHANSU.TỔ chỉ là bản sao để hiển thị (xem
 * planEmployeeTeamSync), đọc nó ở đây thì hai bên lệch nhau là chọn sai mẫu lương.
 */
export function buildTeamsByEmployee(employees, allocations, at) {
    const teams = new Map(employees.map((employee) => [employee.SO_HD, []]));
    for (const allocation of allocations) {
        const soHd = allocation.SO_HD_PHU_TRACH;
        if (!soHd || !teams.has(soHd) || !allocation.TO || !coversDate(allocation, at)) continue;
        teams.get(soHd).push(allocation.TO);
    }
    for (const [soHd, list] of teams) {
        const seen = new Set();
        teams.set(soHd, list.filter((team) => {
            const key = normalizeTeamKey(team);
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        }).sort(compareTeams));
    }
    return teams;
}

/** Giá trị cột NHANSU.TỔ cho một người: các tổ đang phụ trách, cách nhau dấu phẩy ("V2, V11"). */
export const EMPLOYEE_TEAM_SEPARATOR = ', ';

export function splitEmployeeTeams(value) {
    return String(value ?? '').split(',').map((team) => team.trim()).filter(Boolean);
}

/**
 * Những dòng NHANSU cần ghi lại cột TỔ cho khớp phân bổ đang hiệu lực tại `at`. Người không còn
 * phụ trách công đoạn nào thì TỔ về rỗng: đó là sự thật theo phân bổ, không phải mất dữ liệu.
 */
export function planEmployeeTeamSync(employees, allocations, at) {
    const withCode = employees.filter((employee) => employee.SO_HD);
    const teams = buildTeamsByEmployee(withCode, allocations, at);
    return withCode
        .map((employee) => ({ SO_HD: employee.SO_HD, HO_TEN: employee.HO_TEN, from: employee.TO ?? '', TO: (teams.get(employee.SO_HD) ?? []).join(EMPLOYEE_TEAM_SEPARATOR) }))
        .filter((row) => normalizeTeamKey(row.from) !== normalizeTeamKey(row.TO));
}

/** Gắn cột (đã sắp thứ tự) vào mẫu và thêm ID = MA_MAU cho bộ chọn mẫu. */
export function assembleTemplates(templates, templateColumns) {
    const columnsByTemplate = new Map();
    for (const column of templateColumns) {
        columnsByTemplate.set(column.MAU_ID, [...(columnsByTemplate.get(column.MAU_ID) ?? []), column]);
    }
    return templates.map((template) => ({
        ...template,
        ID: template.MA_MAU,
        CHI_TIETS: (columnsByTemplate.get(template.MA_MAU) ?? []).slice().sort((left, right) => (left.THU_TU ?? 0) - (right.THU_TU ?? 0)),
    }));
}

/** Thỏa thuận hiệu lực tại cuối kỳ của từng người; nhiều thỏa thuận cùng phủ thì lấy cái bắt đầu muộn nhất. */
export function resolveAgreementsAt(agreements, at) {
    const result = new Map();
    const sorted = agreements
        .filter((agreement) => agreement.HIEU_LUC_TU && coversDate(agreement, at))
        .sort((left, right) => right.HIEU_LUC_TU - left.HIEU_LUC_TU);
    for (const agreement of sorted) if (!result.has(agreement.NHAN_SU_ID)) result.set(agreement.NHAN_SU_ID, agreement);
    return result;
}

/** Mức đang hiệu lực tại cuối kỳ của từng biến (hằng số hoặc đời công thức); khai chồng thì dòng cập nhật sau thắng. */
export function resolveDatedValuesAt(rows, at) {
    const byCode = new Map();
    for (const row of rows) byCode.set(row.MA_BIEN, [...(byCode.get(row.MA_BIEN) ?? []), row]);
    const result = new Map();
    for (const [code, list] of byCode) {
        const effective = resolveEffectiveDatedRow(
            list.filter((row) => row.HIEU_LUC_TU).map((row) => ({ ...row, UPDATED_AT: row.UPDATED_AT ?? new Date(0) })),
            at,
        );
        if (effective) result.set(canonicalPayrollCode(code), effective);
    }
    return result;
}

/** Phiếu ứng tính vào kỳ: ứng trong kỳ, chưa dùng cho kỳ khác (hoặc đã gắn chính kỳ này), không bị hủy. */
export function selectPeriodAdvances(advances, range, periodId) {
    return advances.filter((advance) => {
        if (!advance.NGAY_UNG || advance.TRANG_THAI === 'HUY') return false;
        const day = startOfDay(advance.NGAY_UNG);
        if (day < startOfDay(range.from) || day > startOfDay(range.to)) return false;
        return advance.TRANG_THAI === 'CHUA_SU_DUNG' || (periodId && advance.BANG_CONG_LUONG_ID === periodId);
    });
}

// Số kiểu ngày của Google Sheets trong khoảng năm 2000–2099. Hằng số tỷ lệ hay hệ số rơi vào khoảng
// này gần như chắc chắn là ô bị Sheet đổi số thập phân "1.5" thành ngày.
const SHEET_DATE_SERIAL_MIN = 36526;
const SHEET_DATE_SERIAL_MAX = 73050;

/** Cảnh báo hằng số bất thường trước khi tính, để một ô sai không làm sai lương cả công ty mà không ai biết. */
export function checkConstantValues(constantRows, variableCatalog) {
    const variableByCode = new Map(variableCatalog.map((variable) => [canonicalPayrollCode(variable.MA_BIEN), variable]));
    const warnings = [];
    for (const [code, row] of constantRows) {
        const dataType = variableByCode.get(code)?.LOAI_DU_LIEU;
        const value = row.GIA_TRI;
        if (value === null || value === undefined || Number.isNaN(value)) {
            warnings.push({ code: 'CONSTANT_INVALID', variableCode: code, blocking: true, message: `Hằng số ${code} không đọc được giá trị số, kiểm tra ô GIÁ TRỊ trên sheet.` });
        } else if ((dataType === 'SO' || dataType === 'TY_LE') && Number.isInteger(value) && value >= SHEET_DATE_SERIAL_MIN && value <= SHEET_DATE_SERIAL_MAX) {
            warnings.push({ code: 'CONSTANT_LOOKS_LIKE_DATE', variableCode: code, blocking: true, message: `Hằng số ${code} = ${value} giống một ngày bị Google Sheets tự đổi (ví dụ gõ "1.5" thành 1/5). Sửa ô GIÁ TRỊ thành số, dùng dấu phẩy thập phân.` });
        } else if (dataType === 'TY_LE' && (value < 0 || value > 100)) {
            warnings.push({ code: 'CONSTANT_RATE_OUT_OF_RANGE', variableCode: code, blocking: true, message: `Tỷ lệ ${code} = ${value}% nằm ngoài khoảng 0–100%.` });
        }
    }
    return warnings;
}

export const TEMPLATE_LEVEL_REASONS = {
    THOA_THUAN: 'Theo thỏa thuận lương',
    NHAN_SU: 'Áp dụng riêng cho nhân sự',
    TO: 'Áp dụng theo tổ',
    NANG_SUAT: 'Áp dụng cho mọi người nhận năng suất',
    TOAN_CONG_TY: 'Áp dụng toàn công ty',
};

export function describePayrollExclusion(reasonCode, teams, excludedByTemplates = []) {
    if (reasonCode === 'THOA_THUAN_MAU_KHAC') {
        return 'Đã có thỏa thuận lương theo một mẫu chưa được chọn cho kỳ này; chọn thêm mẫu đó để đưa họ vào bảng lương.';
    }
    if (reasonCode === 'BI_LOAI_TRU') {
        return `Bị loại trừ khỏi mẫu ${excludedByTemplates.join(', ')}; sửa danh sách loại trừ của mẫu nếu không còn đúng.`;
    }
    if (reasonCode === 'TO_KHONG_CO_MAU') {
        return `Thuộc tổ ${teams.join(', ')} nhưng không mẫu nào phủ tổ này; thêm tổ vào phạm vi mẫu.`;
    }
    return 'Chưa có tổ nào trong hồ sơ nhân sự nên không khớp được mẫu theo tổ; điền tổ cho nhân sự.';
}

/**
 * Chia nhân sự trong kỳ theo mẫu: người có mẫu, người xung đột (khớp nhiều mẫu cùng cấp), người bị loại.
 * input: { employees, teamsByEmployee, agreementByEmployee, selectedTemplates, allTemplates }
 */
export function resolvePeriodEmployees(input) {
    const selectedById = new Map(input.selectedTemplates.map((template) => [template.ID, template]));
    const allById = new Map((input.allTemplates ?? input.selectedTemplates).map((template) => [template.ID, template]));
    const employees = [];
    const excludedEmployees = [];
    const conflicts = [];
    const warnings = [];
    const missingAgreementTemplates = new Map();

    for (const employee of input.employees) {
        const soHd = employee.SO_HD;
        const teams = input.teamsByEmployee.get(soHd) ?? [];
        const team = teams.length ? teams.join(', ') : null;
        const agreement = input.agreementByEmployee.get(soHd);
        const agreementTemplate = agreement ? selectedById.get(agreement.MAU_ID) ?? null : null;
        // Thỏa thuận trỏ vào mẫu đã ngưng thì coi như không có, nếu không người đó không bao giờ có lương.
        const agreementTemplateId = agreement && allById.get(agreement.MAU_ID)?.HOAT_DONG ? agreement.MAU_ID : null;
        const resolution = resolvePayrollTemplateForEmployee({
            employeeId: soHd, teams, agreementTemplate, agreementTemplateId, templates: input.selectedTemplates,
        });
        if (agreement && agreementTemplateId && !agreementTemplate) {
            const bucket = missingAgreementTemplates.get(agreement.MAU_ID) ?? { name: allById.get(agreement.MAU_ID)?.TEN_MAU ?? agreement.MAU_ID, count: 0 };
            bucket.count += 1;
            missingAgreementTemplates.set(agreement.MAU_ID, bucket);
        }
        const base = { employeeId: soHd, employeeCode: soHd, employeeName: employee.HO_TEN, team };
        if (resolution.status === 'CONFLICT') {
            conflicts.push({
                ...base,
                level: resolution.level,
                templates: resolution.templates.map((template) => ({ templateId: template.ID, templateCode: template.MA_MAU, templateName: template.TEN_MAU })),
            });
        } else if (resolution.status === 'EXCLUDED') {
            const excludedBy = resolution.excludedBy.map((template) => template.TEN_MAU);
            excludedEmployees.push({
                ...base,
                reasonCode: resolution.reasonCode,
                reason: describePayrollExclusion(resolution.reasonCode, teams, excludedBy),
                ...(excludedBy.length ? { excludedByTemplates: excludedBy } : {}),
            });
        } else {
            employees.push({
                ...base,
                templateId: resolution.template.ID,
                templateCode: resolution.template.MA_MAU,
                templateName: resolution.template.TEN_MAU,
                templateReason: TEMPLATE_LEVEL_REASONS[resolution.level],
            });
        }
    }

    if (missingAgreementTemplates.size) {
        const missing = Array.from(missingAgreementTemplates, ([templateId, bucket]) => ({ templateId, ...bucket }))
            .sort((left, right) => right.count - left.count);
        const affected = missing.reduce((sum, item) => sum + item.count, 0);
        warnings.push({
            code: 'AGREEMENT_TEMPLATE_NOT_SELECTED',
            blocking: false,
            message: `${affected} nhân sự đã có thỏa thuận lương theo mẫu chưa được chọn (${missing.map((item) => `${item.name}: ${item.count}`).join(', ')}) nên được bỏ qua trong kỳ này. Chọn thêm mẫu của họ nếu muốn tính lương cho họ.`,
            templateIds: missing.map((item) => item.templateId),
        });
    }
    return { employees, excludedEmployees, conflicts, warnings };
}

/**
 * Bộ biên dịch mẫu cho một kỳ: biến dùng đời công thức hiệu lực tại cuối kỳ (chưa có đời nào thì công
 * thức hiện hành), mỗi mẫu biên dịch một lần. Bộ tính và ma trận hiển thị dùng chung để luôn khớp cột.
 */
export function createPeriodTemplateCompiler(variableCatalog, formulaRows) {
    const compiledVariables = variableCatalog.map((variable) => ({
        id: variable.MA_BIEN,
        code: variable.MA_BIEN,
        name: variable.TEN_BIEN,
        kind: variable.LOAI_BIEN,
        source: variable.NGUON_DU_LIEU,
        formula: formulaRows.get(canonicalPayrollCode(variable.MA_BIEN))?.CONG_THUC ?? variable.CONG_THUC,
        active: variable.HOAT_DONG,
    }));
    const cache = new Map();
    return (template) => {
        if (cache.has(template.ID)) return cache.get(template.ID);
        const compiled = compilePayrollTemplate({
            templateId: template.ID,
            columns: template.CHI_TIETS.map((item) => ({
                variableId: item.BIEN_ID,
                formula: item.CONG_THUC,
                order: item.THU_TU,
                required: item.BAT_BUOC,
                visible: item.HIEN_THI,
                allowInput: item.CHO_PHEP_NHAP,
            })),
            variables: compiledVariables,
        });
        if (compiled.errors.length) {
            throw new Error(`Mẫu ${template.MA_MAU} không hợp lệ: ${compiled.errors.map((issue) => issue.message).join(' ')}`);
        }
        cache.set(template.ID, compiled);
        return compiled;
    };
}

/**
 * Dựng đầu vào calculatePayrollPeriod cho từng nhân sự (chuyển từ buildPayrollEmployeeInputs của V2).
 * params: { range, employees, templateByEmployee, variableCatalog, base: { agreementByEmployee,
 *   advances, constantRows, formulaRows }, manualByEmployee, overridesByEmployee, productivityLinesByEmployee,
 *   compileTemplate? }
 * Truyền productivityLinesByEmployee rỗng thì ra bảng tạm tính với lương sản phẩm bằng 0.
 */
export function buildPayrollEmployeeInputs(params) {
    const { base } = params;
    const companyConstantValues = Object.fromEntries(Array.from(base.constantRows, ([code, row]) => [code, row.GIA_TRI]));
    const compileOnce = params.compileTemplate ?? createPeriodTemplateCompiler(params.variableCatalog, base.formulaRows);
    // Kiểu dữ liệu quyết định cách làm tròn: tiền về đồng, giờ tăng ca và ngày công giữ phần lẻ.
    const variableDataTypes = Object.fromEntries(
        params.variableCatalog.map((variable) => [canonicalPayrollCode(variable.MA_BIEN), variable.LOAI_DU_LIEU]),
    );
    // Ngày lễ (NL) theo lịch đã xác nhận, ngày phép (PN) theo đơn đã duyệt; số nhập tay trong kỳ (nếu có) vẫn thắng.
    const leaveDays = paidLeaveDaysByEmployee(base.leaveRequests ?? [], params.range.from, params.range.to);
    const advancesByEmployee = new Map();
    for (const advance of base.advances) {
        advancesByEmployee.set(advance.NHAN_SU_ID, [...(advancesByEmployee.get(advance.NHAN_SU_ID) ?? []), advance]);
    }

    const employeeInputs = params.employees.flatMap((employee) => {
        const soHd = employee.SO_HD;
        const template = params.templateByEmployee.get(soHd);
        if (!template) return [];
        const currentAgreement = base.agreementByEmployee.get(soHd);
        const agreement = currentAgreement?.MAU_ID === template.ID ? currentAgreement : undefined;
        const compiledTemplate = compileOnce(template);
        const manual = { ...(params.manualByEmployee.get(soHd) ?? {}) };
        // Hằng số công ty chỉ điền chỗ trống; số nhập tay riêng của người này trong kỳ vẫn thắng.
        for (const [code, value] of Object.entries(companyConstantValues)) manual[code] ??= value;
        const agreementValues = Object.fromEntries(Object.entries(agreement?.GIA_TRI ?? {}).map(([code, value]) => [canonicalPayrollCode(code), Number(value) || 0]));
        return [{
            employeeId: soHd,
            soHd,
            hoTen: employee.HO_TEN,
            to: employee.TO,
            templateId: template.ID,
            formulas: compiledTemplate.formulas,
            activeCodes: compiledTemplate.activeCodes,
            agreementCodes: compiledTemplate.agreementCodes,
            variableSources: Object.fromEntries(compiledTemplate.columns.map((column) => [column.code, column.source])),
            inputValues: manual,
            productivityLines: params.productivityLinesByEmployee.get(soHd) ?? [],
            attendance: {
                employeeId: soHd,
                workDays: manual.NC ?? 0,
                paidLeaveDays: manual.PN ?? leaveDays.get(soHd) ?? 0,
                paidHolidayDays: manual.NL ?? countPaidHolidayDays(params.range.from, params.range.to, base.holidays ?? [], soHd, splitEmployeeTeams(employee.TO)),
                overtimeHours: manual.GTC ?? 0,
                overtimeRate: manual.DGTC ?? agreementValues.DGTC ?? 0,
            },
            agreement: agreement ? {
                employeeId: soHd,
                templateCode: template.MA_MAU,
                effectiveFrom: agreement.HIEU_LUC_TU,
                effectiveTo: agreement.HIEU_LUC_DEN,
                values: agreementValues,
            } : null,
            advances: (advancesByEmployee.get(soHd) ?? []).map((item) => ({ id: item.MA_PHIEU, employeeId: soHd, date: item.NGAY_UNG, amount: item.SO_TIEN ?? 0 })),
            overrides: params.overridesByEmployee?.get(soHd),
        }];
    });
    return { employeeInputs, variableDataTypes };
}

// Một lượt tính duy nhất: TNCN là biến công thức nên bộ giải tự xếp đúng thứ tự TC, TNCT, TNTT, TNCN, TCHI.
export function runPayrollCalculation(inputs) {
    return calculatePayrollPeriod({ employees: inputs.employeeInputs, variableDataTypes: inputs.variableDataTypes });
}

/**
 * Gộp dòng năng suất của một người theo tổ–công đoạn và đơn giá để lưu vào CHI TIẾT NĂNG SUẤT. Bộ tính
 * chỉ cần tổng THANH_TIEN (LSP) nên bản gộp đủ để chốt lại ra đúng số, mà không phải lưu từng ngày.
 */
export function summarizeProductivityLines(lines) {
    const groups = new Map();
    for (const line of lines) {
        const key = [line.TO_PBNS_ID, line.TO, line.MA_CONG_DOAN, line.TEN_CONG_DOAN, line.DON_VI_TINH, line.DON_GIA, line.TIEN_HIEU_CHINH].join('|');
        const group = groups.get(key) ?? {
            TO_PBNS_ID: line.TO_PBNS_ID,
            TO: line.TO ?? '',
            MA_CONG_DOAN: line.MA_CONG_DOAN,
            TEN_CONG_DOAN: line.TEN_CONG_DOAN,
            DON_VI_TINH: line.DON_VI_TINH,
            DON_GIA: line.DON_GIA,
            TIEN_HIEU_CHINH: line.TIEN_HIEU_CHINH,
            KHOI_LUONG: 0,
            THANH_TIEN: 0,
            SO_DONG: 0,
        };
        group.KHOI_LUONG += line.KHOI_LUONG;
        group.THANH_TIEN += line.THANH_TIEN;
        group.SO_DONG += 1;
        groups.set(key, group);
    }
    return Array.from(groups.values()).map((group) => ({ ...group, KHOI_LUONG: Number(group.KHOI_LUONG.toFixed(6)) }));
}
